import React, { Fragment } from "react";
import { makeStyles, Theme, createStyles, TableCell, IconButton } from "@material-ui/core";
import clsx from 'clsx';
import { observer } from "mobx-react";
import { StubOrganizationVolunteer } from "../../../../../stores/models/StubOrganizationVolunteer";
import RecordTable from "../RecordTable";
import { ISortableTableHeader, TableHeader } from "../../../../../stores/models/TableHeader";
import { OptionCollection } from "../../../../../stores/models/OptionCollection";
import { Option } from "../../../../../stores/models/Option";
import EmailField from "./InputFields/EmailField";
import NameField from "./InputFields/NameField";
import BirthdateField from "./InputFields/BirthdateField";
import StatusField from "./InputFields/StatusField";
import { Delete } from "mdi-material-ui";
import { Alert, AlertTitle } from "@material-ui/lab";
import MultipleChoiceField from "./InputFields/MultipleChoiceField";
import { IVolunteerImportTemplate } from "../../../../../stores/models/interfaces/IVolunteerImportTemplate";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formFields: {
            display: 'flex',
            flexDirection: 'column',
            '& > *': {
                '&:not(:last-child)': {
                    marginBottom: theme.spacing(2),
                },
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                '& > *': {
                    flexGrow: 1
                }
            }
        },
        iconButton: {
            padding: 0
        },
        alert: {
            marginBottom: theme.spacing(2)
        }
    })
);

interface ImportVolunteersFormProps extends React.HTMLAttributes<HTMLDivElement> {
    volunteerImportTemplate: IVolunteerImportTemplate;
    volunteerCollection: OptionCollection<'id', StubOrganizationVolunteer>;
    error?: boolean;
}

const ImportVolunteersForm = observer((props: ImportVolunteersFormProps) => {

    const classes = useStyles();

    /********** Table Headers *********/

    const headerCellData = props.volunteerImportTemplate.fields.map(field => {
        const headerCell: ISortableTableHeader<StubOrganizationVolunteer> = {
            id: field.id,
            alignment: 'left',
            disablePadding: false,
            label: field.required ? field.header + "*" : field.header,
            sortable: false
        }
        return headerCell;
    }).concat({ id: 'delete', alignment: 'center', disablePadding: false, label: 'Delete', sortable: false })
    const headCells = headerCellData.map(headCell => new TableHeader(headCell));

    /********** Display Functions *********/

    const deleteCell = (option: Option<StubOrganizationVolunteer>, rowIndex: number, cellIndex: number) => {
        return (
            <TableCell align="center" key={`${rowIndex}-${cellIndex}`}>
                <IconButton
                    className={classes.iconButton}
                    onClick={(event) => deleteRow(event, option.object)}
                >
                    <Delete color="action" />
                </IconButton>
            </TableCell>
        );
    }

    const displayFunctions = props.volunteerImportTemplate.fields.map(field => {
        return (option: Option<StubOrganizationVolunteer>, rowIndex: number, cellIndex: number) => {

            let cellContents;
            switch (field.id) {
                case 'firstName':
                case 'lastName':
                    cellContents = <NameField volunteer={option.object} nameType={field.id} />
                    break;
                case 'email':
                    cellContents = <EmailField volunteer={option.object} />
                    break;
                case 'birthdate':
                    cellContents = <BirthdateField volunteer={option.object} />
                    break;
                case 'statusId':
                    cellContents = <StatusField volunteer={option.object} />
                    break;
                case 'registrationFormStatusId':
                    cellContents = <MultipleChoiceField volunteer={option.object} options={field.options || []} />
                    break;
            }

            return (
                <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>
                    {cellContents}
                </TableCell>
            );
        }
    }).concat(deleteCell);


    /********** Event Handler *********/

    const deleteRow = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, volunteer: StubOrganizationVolunteer) => {
        props.volunteerCollection.removeOptionById(volunteer.id);
    }

    /********** Render *********/

    return (
        <Fragment>
            <Alert severity="info" className={classes.alert}>
                <AlertTitle>Email Invitations</AlertTitle>
                All volunteers imported with an email address, except those with a "Banned" status, will be sent an invitation to link to your organization.
            </Alert>
            <div className={clsx(classes.formFields, props.className)}>
                <RecordTable
                    records={props.volunteerCollection}
                    tableHeaderCells={headCells}
                    displayFunctions={displayFunctions}
                    onRowClicked={(option: Option<StubOrganizationVolunteer>, rowIndex: number) => {
                        // No action
                    }}
                />
            </div>
        </Fragment>
    )
});

export default ImportVolunteersForm;